import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useBlockedFeatures } from 'src/hooks/useBlockedFeatures';
import { dt } from 'src/utils/documentsI18n';
import './PaymentMethodDropdown.scss';

const PaymentMethodDropdown = ({
  onSelect,
  disabled = false,
  isReceived = true,
  variant = 'light',
  direction = 'down',
  allowedMethods = null,
  compact = false,
  triggerVariant = 'default', // 'default' | 'kebab'
  actionLabelPrefix = null,   // e.g. 'Abonar con' -> 'Abonar con Tarjeta de Crédito'
  align = 'start',            // 'start' | 'end' — menu horizontal alignment vs trigger
}) => {
  const { isAbonarBlocked } = useBlockedFeatures();
  const isDisabled = disabled || isAbonarBlocked();
  const [isOpen, setIsOpen] = useState(false);
  const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0, transform: undefined });
  const dropdownRef = useRef(null);
  const buttonRef = useRef(null);
  const menuRef = useRef(null);

  const allPaymentMethods = [
    { id: 'efectivo', labelKey: 'reconcile.paymentMethods.cash', implemented: true },
    { id: 'tarjeta_credito', labelKey: 'reconcile.paymentMethods.creditCard', implemented: true },
    { id: 'pasarela_pago', labelKey: 'reconcile.paymentMethods.paymentGateway', implemented: true },
    { id: 'cheque', labelKey: 'reconcile.paymentMethods.check', implemented: true },
    { id: 'tercero', labelKey: 'reconcile.paymentMethods.thirdParty', implemented: true },
    { id: 'factoring', labelKey: 'reconcile.paymentMethods.factoring', implemented: true },
    { id: 'diferencia_tipo_cambio', labelKey: 'reconcile.paymentMethods.fxDifference', implemented: true },
    { id: 'cuenta_internacional', labelKey: 'reconcile.paymentMethods.international', implemented: false },
  ];

  const buildOptionLabel = (method) => {
    const methodLabel = dt(method.labelKey);
    if (!actionLabelPrefix) return methodLabel;
    const normalized = String(methodLabel || '').replace(/^(en|con|por|in|via|by)\s+/i, '').trim();
    return normalized ? `${actionLabelPrefix} ${normalized}` : actionLabelPrefix;
  };

  // Filter payment methods: hide cuenta_internacional; factoring only when por cobrar (isReceived false); pasarela for both sides
  const paymentMethods = useMemo(() => {
    let filtered = allPaymentMethods.filter(method => {
      if (method.id === 'cuenta_internacional') return false;
      if (method.id === 'factoring') {
        return isReceived === false;
      }
      return true;
    });
    
    // Further filter by allowedMethods if provided
    if (allowedMethods && Array.isArray(allowedMethods)) {
      filtered = filtered.filter(method => allowedMethods.includes(method.id));
    }
    
    return filtered;
  }, [isReceived, allowedMethods]);

  // Calculate menu position when opening and on scroll/resize
  useEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const updatePosition = () => {
      if (!buttonRef.current) return;
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const menuWidth = menuRef.current?.offsetWidth || 300;
      const menuHeight = menuRef.current?.offsetHeight || 220;
      const viewportPadding = 12;

      const openUp = direction === 'up';
      let top = openUp ? buttonRect.top - 6 : buttonRect.bottom + 6;
      let transform = openUp ? 'translateY(-100%)' : undefined;

      if (!openUp && top + menuHeight > window.innerHeight - viewportPadding) {
        top = buttonRect.top - 6;
        transform = 'translateY(-100%)';
      } else if (openUp && top - menuHeight < viewportPadding) {
        top = buttonRect.bottom + 6;
        transform = undefined;
      }

      let left =
        align === 'end'
          ? buttonRect.right - menuWidth
          : buttonRect.left;

      if (left + menuWidth > window.innerWidth - viewportPadding) {
        left = window.innerWidth - menuWidth - viewportPadding;
      }
      if (left < viewportPadding) {
        left = viewportPadding;
      }

      setMenuPosition({ top, left, transform });
    };

    // Initial position
    updatePosition();

    // Update on scroll/resize
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isOpen, direction, align]);

  // Reposition once menu is mounted (real width/height)
  useEffect(() => {
    if (!isOpen || !menuRef.current || !buttonRef.current) return;
    const buttonRect = buttonRef.current.getBoundingClientRect();
    const menuWidth = menuRef.current.offsetWidth || 300;
    const menuHeight = menuRef.current.offsetHeight || 220;
    const viewportPadding = 12;
    const openUp = direction === 'up';
    let top = openUp ? buttonRect.top - 6 : buttonRect.bottom + 6;
    let transform = openUp ? 'translateY(-100%)' : undefined;
    if (!openUp && top + menuHeight > window.innerHeight - viewportPadding) {
      top = buttonRect.top - 6;
      transform = 'translateY(-100%)';
    }
    let left = align === 'end' ? buttonRect.right - menuWidth : buttonRect.left;
    if (left + menuWidth > window.innerWidth - viewportPadding) {
      left = window.innerWidth - menuWidth - viewportPadding;
    }
    if (left < viewportPadding) left = viewportPadding;
    setMenuPosition({ top, left, transform });
  }, [isOpen, direction, align, paymentMethods.length]);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownRef.current && 
        !dropdownRef.current.contains(event.target) &&
        menuRef.current &&
        !menuRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      // Use setTimeout to avoid immediate closure
      setTimeout(() => {
        document.addEventListener('mousedown', handleClickOutside);
      }, 0);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
      buttonRef.current?.blur();
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      if (!isOpen) {
        setIsOpen(true);
      }
    }
  };

  const handleOptionClick = (method) => {
    if (method.implemented) {
      setIsOpen(false);
      if (onSelect) {
        onSelect(method);
      }
    } else {
      // Show "Not implemented yet" message
      alert(dt('reconcile.paymentMethods.notImplemented'));
      setIsOpen(false);
    }
  };

  const handleButtonClick = () => {
    if (!isDisabled) {
      setIsOpen(!isOpen);
    }
  };

  const isDark = variant === 'dark';
  const isKebab = triggerVariant === 'kebab';

  return (
    <div 
      ref={dropdownRef}
      className={`payment-method-dropdown ${isDisabled ? 'disabled' : ''} ${isOpen ? 'open' : ''} ${isDark ? 'variant-dark' : ''} ${compact ? 'compact' : ''}`}
    >
      <div className="payment-method-dropdown-wrapper">
        <button
          ref={buttonRef}
          type="button"
          className={`payment-method-dropdown-trigger ${isDark ? 'variant-dark' : ''} ${isKebab ? 'trigger-kebab' : ''}`}
          onClick={handleButtonClick}
          onKeyDown={handleKeyDown}
          disabled={isDisabled}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          aria-label={isKebab ? (actionLabelPrefix ? `${actionLabelPrefix}...` : 'Más acciones') : undefined}
          tabIndex={isDisabled ? -1 : 0}
        >
          {isKebab ? (
            <span className="payment-method-dropdown-kebab" aria-hidden="true">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none">
                <circle cx="12" cy="5" r="1.8" fill={isDark ? "#e5e7eb" : "#6b7280"} />
                <circle cx="12" cy="12" r="1.8" fill={isDark ? "#e5e7eb" : "#6b7280"} />
                <circle cx="12" cy="19" r="1.8" fill={isDark ? "#e5e7eb" : "#6b7280"} />
              </svg>
            </span>
          ) : (
            <>
              <span className="payment-method-dropdown-text">{dt('reconcile.payInstallment')}</span>
              <span className="payment-method-dropdown-arrow">
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 20 20">
                  <path stroke={isDark ? "#e5e7eb" : "#6b7280"} strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="m6 8 4 4 4-4"/>
                </svg>
              </span>
            </>
          )}
        </button>
      </div>
      {isOpen && !isDisabled && typeof document !== 'undefined' && createPortal(
        <div 
          ref={menuRef}
          className={`payment-method-dropdown-menu payment-method-dropdown-menu-portal ${isDark ? 'variant-dark' : ''} ${direction === 'up' ? 'direction-up' : ''}`}
          style={{
            position: 'fixed',
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`,
            ...(direction === 'up' && menuPosition.transform ? { transform: menuPosition.transform } : {}),
          }}
        >
          <ul 
            className={`payment-method-dropdown-options ${isDark ? 'variant-dark' : ''}`}
            role="listbox"
          >
            {paymentMethods.map((method) => (
              <li
                key={method.id}
                className={`payment-method-option ${method.implemented ? '' : 'disabled'} ${isDark ? 'variant-dark' : ''}`}
                onClick={() => handleOptionClick(method)}
                role="option"
                tabIndex={method.implemented ? 0 : -1}
              >
                <span className="payment-method-option-text">{buildOptionLabel(method)}</span>
              </li>
            ))}
          </ul>
        </div>,
        document.body
      )}
    </div>
  );
};

export default PaymentMethodDropdown;

